import { locationLabel } from './location';
import { RADIO_TECHNOLOGIES, collectProviders, ratePoint, } from './measurement';
export const NOTICE_CODES = ['locationMissing', 'seriesMissing', 'empty'];
export const LOCATION_MISSING_TEXT = 'Ort nicht mehr vorhanden';
export const SERIES_MISSING_TEXT = 'Messreihe nicht mehr vorhanden';
export const UNLOCATED_TITLE = 'Ohne Ortszuordnung';
const NO_BEACONS_TEXT = 'Keine Beacons im gewählten Umfang.';
const NO_SERIES_TEXT = 'Keine Messreihe gewählt.';
function notice(code, text, seriesId) {
    return { kind: 'notice', code, text, ...(seriesId ? { seriesId } : {}) };
}
/** `2026-10-12` -> `12.10.2026`; anything else unchanged. */
export function formatGermanDate(isoDate) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
    return match ? `${match[3]}.${match[2]}.${match[1]}` : isoDate;
}
function compareLocations(a, b) {
    return a.order - b.order || locationLabel(a).localeCompare(locationLabel(b), 'de');
}
function indexLocations(locations) {
    const byId = new Map(locations.map((node) => [node.id, node]));
    const childMap = new Map();
    for (const node of locations) {
        if (!node.parentLocationId)
            continue;
        const list = childMap.get(node.parentLocationId) ?? [];
        list.push(node);
        childMap.set(node.parentLocationId, list);
    }
    for (const list of childMap.values())
        list.sort(compareLocations);
    return { byId, children: (id) => childMap.get(id) ?? [] };
}
/** The node and its whole subtree; a corrupt cycle is visited once. */
function subtreeIds(index, node) {
    const ids = new Set();
    const visit = (current) => {
        if (ids.has(current.id))
            return;
        ids.add(current.id);
        for (const child of index.children(current.id))
            visit(child);
    };
    visit(node);
    return ids;
}
/** Breadcrumb below the document location, e.g. "Halle 1 › EG"; "–" without one. */
export function scopedLocationLabel(locationId, byId, rootId) {
    const node = locationId ? byId.get(locationId) : undefined;
    if (!node)
        return '–';
    const start = node.path.indexOf(rootId);
    const below = start >= 0 ? node.path.slice(start + 1) : node.path;
    const ids = below.length > 0 ? below : [node.id];
    const label = ids
        .map((id) => byId.get(id))
        .filter((entry) => entry !== undefined)
        .map((entry) => locationLabel(entry))
        .filter((text) => text.length > 0)
        .join(' › ');
    return label || '–';
}
/** Plans in location tree order, then by their own order and name. */
function comparePlans(byId) {
    const key = (plan) => (byId.get(plan.locationId)?.path ?? []).map((id) => byId.get(id)?.order ?? 0);
    return (a, b) => {
        const left = key(a);
        const right = key(b);
        for (let i = 0; i < Math.min(left.length, right.length); i += 1) {
            const difference = (left[i] ?? 0) - (right[i] ?? 0);
            if (difference !== 0)
                return difference;
        }
        return left.length - right.length || a.order - b.order || a.name.localeCompare(b.name, 'de');
    };
}
function planFigure(plan, markers) {
    return {
        planId: plan.id,
        name: plan.name,
        locationId: plan.locationId,
        image: {
            assetId: plan.currentImage.assetId,
            width: plan.currentImage.width,
            height: plan.currentImage.height,
        },
        markers,
    };
}
function beaconCard(beacon, byId, rootId) {
    const hardware = beacon.hardware;
    return {
        beaconId: beacon.id,
        beaconName: beacon.beaconName,
        sequenceNumber: beacon.sequenceNumber,
        ...(beacon.locationId ? { locationId: beacon.locationId } : {}),
        locationLabel: scopedLocationLabel(beacon.locationId, byId, rootId),
        ...(beacon.positionDescription ? { positionDescription: beacon.positionDescription } : {}),
        ...(beacon.mountingNote ? { mountingNote: beacon.mountingNote } : {}),
        ...(hardware?.serialNumber ? { serialNumber: hardware.serialNumber } : {}),
        ...(hardware?.macAddress ? { macAddress: hardware.macAddress } : {}),
        ...(hardware?.uuid ? { uuid: hardware.uuid } : {}),
    };
}
/**
 * Chapter "Beacon-Positionen": one generated subsection per child of the
 * document location (usually the buildings), below it one per grandchild
 * (floors, areas) with that grandchild's whole subtree. Each holds the plans
 * of its locations that carry beacon markers, then one card per beacon.
 * Locations without beacons are left out; beacons without a location close
 * the chapter under "Ohne Ortszuordnung".
 */
export function generateBeaconPositions(scope, data) {
    const index = indexLocations(data.locations);
    const root = index.byId.get(scope.siteLocationId);
    if (!root)
        return { items: [notice('locationMissing', LOCATION_MISSING_TEXT)], sections: [] };
    const beaconsById = new Map(data.beacons.map((entry) => [entry.id, entry]));
    const markersByPlan = new Map();
    for (const placement of data.placements) {
        if (placement.refType !== 'beacon')
            continue;
        const placed = beaconsById.get(placement.refId);
        if (!placed)
            continue;
        const markers = markersByPlan.get(placement.planId) ?? [];
        markers.push({
            kind: 'beacon',
            refId: placed.id,
            label: placed.beaconName,
            x: placement.x,
            y: placement.y,
        });
        markersByPlan.set(placement.planId, markers);
    }
    const plans = [...data.plans].sort(comparePlans(index.byId));
    const beacons = [...data.beacons].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
    const content = (locationIds) => {
        const items = [];
        const figures = plans
            .filter((entry) => locationIds.has(entry.locationId) && markersByPlan.has(entry.id))
            .map((entry) => planFigure(entry, markersByPlan.get(entry.id) ?? []));
        if (figures.length > 0)
            items.push({ kind: 'plans', plans: figures });
        const cards = beacons
            .filter((entry) => entry.locationId !== undefined && locationIds.has(entry.locationId))
            .map((entry) => beaconCard(entry, index.byId, root.id));
        if (cards.length > 0)
            items.push({ kind: 'beaconCards', cards });
        return items;
    };
    const sections = [];
    for (const first of index.children(root.id)) {
        const children = [];
        for (const second of index.children(first.id)) {
            const items = content(subtreeIds(index, second));
            if (items.length > 0) {
                children.push({
                    key: `loc:${second.id}`,
                    title: locationLabel(second),
                    locationId: second.id,
                    items,
                    children: [],
                });
            }
        }
        const items = content(new Set([first.id]));
        if (items.length > 0 || children.length > 0) {
            sections.push({
                key: `loc:${first.id}`,
                title: locationLabel(first),
                locationId: first.id,
                items,
                children,
            });
        }
    }
    const unlocated = beacons.filter((entry) => !entry.locationId || !index.byId.has(entry.locationId));
    if (unlocated.length > 0) {
        sections.push({
            key: 'unlocated',
            title: UNLOCATED_TITLE,
            items: [
                {
                    kind: 'beaconCards',
                    cards: unlocated.map((entry) => beaconCard(entry, index.byId, root.id)),
                },
            ],
            children: [],
        });
    }
    const items = content(new Set([root.id]));
    if (items.length === 0 && sections.length === 0)
        items.push(notice('empty', NO_BEACONS_TEXT));
    return { items, sections };
}
/**
 * One column per technology x provider that occurs in the measurements;
 * technologies in RADIO_TECHNOLOGIES order, providers alphabetically.
 */
export function measurementColumns(measurements) {
    const present = new Set();
    for (const entry of measurements) {
        for (const cell of entry.cells)
            present.add(`${cell.technology}|${cell.provider}`);
    }
    const providers = collectProviders(measurements);
    const columns = [];
    for (const technology of RADIO_TECHNOLOGIES) {
        for (const provider of providers) {
            if (present.has(`${technology}|${provider}`)) {
                columns.push({ technology, provider, label: `${provider} ${technology}` });
            }
        }
    }
    return columns;
}
/** Strongest cell of the column, rated as in A. */
function cellOf(entry, column) {
    if (!entry)
        return { level: null, rsrp: null };
    const rating = ratePoint(entry.cells, [column.technology], [column.provider])[0];
    return { level: rating?.level ?? null, rsrp: rating?.rsrp ?? null };
}
/** Live, not obsolete points of the series by number, with their measurements. */
function seriesData(target, data) {
    const points = data.points
        .filter((entry) => entry.seriesId === target.id && entry.status !== 'obsolete')
        .sort((a, b) => a.sequenceNumber - b.sequenceNumber);
    // Only measurements of the listed points: an obsolete point must not add
    // a column that stays empty ("---") in every row.
    const pointIds = new Set(points.map((entry) => entry.id));
    const measurements = data.measurements.filter((entry) => entry.seriesId === target.id && pointIds.has(entry.measurementPointId));
    return {
        points,
        measurementByPoint: new Map(measurements.map((entry) => [entry.measurementPointId, entry])),
        columns: measurementColumns(measurements),
    };
}
/**
 * Chapter "Funkmesspunkte": one generated subsection per selected series, in
 * the order of the selection. It holds the plans with points of the series
 * (neutral, point numbers), then the table with the traffic light per column.
 */
export function generateMeasurementPoints(scope, data) {
    if (scope.seriesIds.length === 0)
        return { items: [notice('empty', NO_SERIES_TEXT)], sections: [] };
    const index = indexLocations(data.locations);
    const seriesById = new Map(data.series.map((entry) => [entry.id, entry]));
    const plans = [...data.plans].sort(comparePlans(index.byId));
    const items = [];
    const sections = [];
    for (const seriesId of scope.seriesIds) {
        const target = seriesById.get(seriesId);
        if (!target) {
            items.push(notice('seriesMissing', SERIES_MISSING_TEXT, seriesId));
            continue;
        }
        const { points, measurementByPoint, columns } = seriesData(target, data);
        const pointById = new Map(points.map((entry) => [entry.id, entry]));
        const markersByPlan = new Map();
        for (const placement of data.placements) {
            if (placement.refType !== 'measurementPoint' || placement.seriesId !== target.id)
                continue;
            const placed = pointById.get(placement.refId);
            if (!placed)
                continue;
            const markers = markersByPlan.get(placement.planId) ?? [];
            markers.push({
                kind: 'point',
                refId: placed.id,
                label: String(placed.sequenceNumber),
                x: placement.x,
                y: placement.y,
            });
            markersByPlan.set(placement.planId, markers);
        }
        const figures = plans
            .filter((entry) => markersByPlan.has(entry.id))
            .map((entry) => planFigure(entry, markersByPlan.get(entry.id) ?? []));
        const rows = points.map((entry) => ({
            pointId: entry.id,
            sequenceNumber: entry.sequenceNumber,
            ...(entry.locationNote ? { locationNote: entry.locationNote } : {}),
            locationLabel: scopedLocationLabel(entry.locationId, index.byId, scope.siteLocationId),
            cells: columns.map((column) => cellOf(measurementByPoint.get(entry.id), column)),
        }));
        const sectionItems = [];
        if (figures.length > 0)
            sectionItems.push({ kind: 'plans', plans: figures });
        sectionItems.push({ kind: 'measurementTable', table: { seriesId: target.id, columns, rows } });
        sections.push({
            key: `series:${target.id}`,
            title: `${target.label} – ${formatGermanDate(target.measuredOn)}`,
            seriesId: target.id,
            items: sectionItems,
            children: [],
        });
    }
    return { items, sections };
}
/**
 * Chapter "Zusammenfassung": beacons in scope (subtree plus beacons without
 * location, the set chapter "Beacon-Positionen" shows), points per series and
 * the distribution of levels per provider and technology.
 */
export function generateSummary(scope, data) {
    const index = indexLocations(data.locations);
    const root = index.byId.get(scope.siteLocationId);
    const items = [];
    if (!root)
        items.push(notice('locationMissing', LOCATION_MISSING_TEXT));
    const beaconCount = root
        ? data.beacons.filter((entry) => {
            const node = entry.locationId ? index.byId.get(entry.locationId) : undefined;
            return !node || node.path.includes(root.id);
        }).length
        : null;
    const seriesById = new Map(data.series.map((entry) => [entry.id, entry]));
    const summaries = [];
    for (const seriesId of scope.seriesIds) {
        const target = seriesById.get(seriesId);
        if (!target) {
            items.push(notice('seriesMissing', SERIES_MISSING_TEXT, seriesId));
            continue;
        }
        const { points, measurementByPoint, columns } = seriesData(target, data);
        const distribution = columns.map((column) => {
            const byLevel = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
            let none = 0;
            for (const entry of points) {
                const cell = cellOf(measurementByPoint.get(entry.id), column);
                if (cell.level === null)
                    none += 1;
                else
                    byLevel[cell.level] += 1;
            }
            return { column, byLevel, none };
        });
        summaries.push({
            seriesId: target.id,
            label: target.label,
            measuredOn: target.measuredOn,
            pointCount: points.length,
            distribution,
        });
    }
    items.push({ kind: 'summary', summary: { beaconCount, series: summaries } });
    return { items, sections: [] };
}
export function generateContent(kind, scope, data) {
    switch (kind) {
        case 'beaconPositions':
            return generateBeaconPositions(scope, data);
        case 'measurementPoints':
            return generateMeasurementPoints(scope, data);
        case 'summary':
            return generateSummary(scope, data);
    }
}
//# sourceMappingURL=document-generators.js.map